/**
 * Journey Create Schemas
 *
 * Cornerstone refactor 2026-05: `unlockMode` removed. Tier is derived
 * server-side from `fundingSource`; clients never pick it. See
 * `tierOfFunding()` in `@tonnie/foundation`.
 */

import { z } from 'zod';

import { openDestinationCountrySchema } from '../validators';

export const journeyScenarioSchema = z.enum([
  'pre_immigration',
  'already_abroad',
  'relocation_only',
  'integration_only',
]);

export const heroTypeSchema = z.enum(['checkup', 'teleportal', 'qultureup', 'care', 'passport']);

/**
 * The household a journey is built for — the shape the platform stores
 * (`journey_instances.household_config`, `HouseholdConfig` in @tonnie/types).
 */
export const householdConfigSchema = z.object({
  familySize: z.number().int().min(1).max(20).default(1),
  hasSpouse: z.boolean().default(false),
  hasChildren: z.boolean().default(false),
  childrenCount: z.number().int().min(0).max(15).optional(),
  childrenAges: z.array(z.number().int().min(0).max(25)).optional(),
});

export type HouseholdConfig = z.infer<typeof householdConfigSchema>;

/** `POST /journeys` intent (R2 · R10) — see `createJourneySchema`. */
export const createJourneyIntentSchema = z.enum(['new', 'resolve']);

export type CreateJourneyIntent = z.infer<typeof createJourneyIntentSchema>;

/**
 * The citizen's name for a journey (D-F, `journey_instances.label`): what tells
 * two journeys on the same route apart. Trimmed; 1–40 characters.
 */
export const journeyLabelSchema = z
  .string()
  .trim()
  .min(1, 'validation.journey.labelRequired')
  .max(40, 'validation.journey.labelTooLong');

/** `PUT /journeys/:id/label` — set the name, or clear it with `null`. */
export const setJourneyLabelSchema = z.object({
  label: journeyLabelSchema.nullable(),
});

export type SetJourneyLabelInput = z.infer<typeof setJourneyLabelSchema>;

/**
 * The rename form (D-F): one text field, where an EMPTY name clears it. The
 * form submits `{ label: name || null }` through `setJourneyLabelSchema`.
 */
export const journeyLabelFormSchema = z.object({
  label: z.string().trim().max(40, 'validation.journey.labelTooLong'),
});

export type JourneyLabelFormInput = z.infer<typeof journeyLabelFormSchema>;

/** `GET /journeys/route-advice` — the route the citizen is about to build (R4). */
export const journeyRouteAdviceQuerySchema = z.object({
  origin: z.string().length(2, 'validation.countryCode.length'),
  destination: openDestinationCountrySchema,
});

export type JourneyRouteAdviceQuery = z.infer<typeof journeyRouteAdviceQuerySchema>;

/**
 * `POST /journeys` — the ONE contract for a citizen building a journey.
 *
 * platform-api's controller validates with exactly this schema. It used to keep
 * its own copy, which this one had drifted from (a household of
 * `hasPartner`/`hasPets` nobody stores) — and when open destinations were
 * added HERE, the live door never saw it: a closed destination passed the
 * request body and was only refused deeper, by the journey factory (cold run
 * 3, 29 Sep, block 56 S16).
 */
export const createJourneySchema = z
  .object({
    originCountry: z.string().length(2, 'validation.countryCode.length'),
    destinationCountry: openDestinationCountrySchema,
    scenario: journeyScenarioSchema,
    // Cornerstone refactor 2026-05: `unlockMode` + `accessLevel` are gone.
    // Self-signup journeys are always `funding_source='self_free'` at creation;
    // upgrades happen via the billing flows.
    householdConfig: householdConfigSchema.optional(),
    heroesEnabled: z
      .array(heroTypeSchema)
      .optional()
      .default(['checkup', 'teleportal', 'qultureup', 'care', 'passport']),
    programId: z.uuid().optional(),
    cohortId: z.uuid().optional(),
    /**
     * What the citizen asked for (JOURNEYS-MULTI-ROUTE R2 · R10):
     *   • `'new'` — "Build" / "Build another": always a new journey, idempotent
     *     on `requestId` (R3) and capped per origin → destination (D-A);
     *   • absent or `'resolve'` — the legacy question old store builds ask:
     *     the journey already on this origin → destination is reused.
     */
    intent: createJourneyIntentSchema.optional(),
    /** The client's id for this create (R3) — required with `intent: 'new'`, and only there. */
    requestId: z.uuid().optional(),
    /** The citizen's optional name for the new journey (D-F) — only with `intent: 'new'`. */
    label: journeyLabelSchema.optional(),
  })
  .superRefine((body, ctx) => {
    if (body.intent === 'new' && !body.requestId) {
      ctx.addIssue({
        code: 'custom',
        path: ['requestId'],
        message: 'validation.journey.requestIdRequired',
      });
    }
    if (body.intent !== 'new' && (body.requestId !== undefined || body.label !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: [body.requestId !== undefined ? 'requestId' : 'label'],
        message: 'validation.journey.newIntentOnly',
      });
    }
  });

export type CreateJourneyInput = z.infer<typeof createJourneySchema>;
